import { cn } from '@folio/lib';
import { Popover, PopoverContent, PopoverTrigger } from '@folio/components/ui/popover/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@folio/components/ui/tooltip/tooltip';
import type { DocumentClassificationState, AIClassificationDisplayConfig } from 'korint-sdk/react';
import {
  AlertCircleIcon,
  SparklesIcon,
  PenIcon,
  Loader2Icon,
  RotateCwIcon,
  ShieldAlertIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { Button } from '../../ui/button';
import { formatExtractionError } from '../document-extraction/format-extraction-error';
import {
  DocumentProcessingStatus,
  isDocumentClassificationProcessing,
} from './DocumentProcessingStatus';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export interface AIClassificationMetadata {
  status: 'auto_accepted' | 'pending_review' | 'manually_corrected';
  suggestedCategory: string;
  suggestedcategory?: string;
  traceId?: string;
  reasoning: string;
  qualityFlags?: string[];
  side?: string;
  judgements?: {
    consensus: boolean;
    verdicts?: Array<{ judge: string; agrees: boolean; reasoning: string }>;
  };
  correction?: {
    correctedBy: string;
    correctedAt: string;
    originalCategory: string;
    newCategory: string;
  };
}

export function parseAIClassificationMetadata(raw: unknown): AIClassificationMetadata | undefined {
  if (!raw) return undefined;

  let parsed: Record<string, unknown>;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return undefined;
    }
  } else if (typeof raw === 'object') {
    parsed = raw as Record<string, unknown>;
  } else {
    return undefined;
  }

  if (!parsed.status || !parsed.reasoning) return undefined;

  return {
    status: parsed.status as AIClassificationMetadata['status'],
    suggestedCategory: (parsed.suggestedCategory as string) ?? (parsed.suggestedcategory as string),
    suggestedcategory: parsed.suggestedcategory as string | undefined,
    traceId: parsed.traceId as string | undefined,
    reasoning: parsed.reasoning as string,
    qualityFlags: (parsed.qualityFlags as string[]) ?? (parsed.qualityflags as string[]) ?? [],
    side: parsed.side as string | undefined,
    judgements: parsed.judgements as AIClassificationMetadata['judgements'],
    correction: parsed.correction as AIClassificationMetadata['correction'],
  };
}

export function translateComplianceMessage(
  message: string,
  metadata: Record<string, unknown> | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (message === 'missing_required_fields' && Array.isArray(metadata?.missingFields)) {
    const translatedFields = (metadata.missingFields as string[]).map(field =>
      t(`documents.extraction.fields.${field}`, { defaultValue: field }),
    );
    return translatedFields.join(', ');
  }
  const translated = t(`documents.validity.messages.${message}`, { defaultValue: '' });
  return translated || message;
}

type ExtractionBadgeStatus = 'PENDING' | 'DONE' | 'FAILED' | 'SKIPPED';

interface ExtractionBadgeData {
  status: ExtractionBadgeStatus;
  fields?: Record<string, unknown>;
  errorMessage?: string;
  onRetry?: () => void;
  isRetrying?: boolean;
}

interface AIClassificationBadgeProps {
  classificationState?: DocumentClassificationState;
  metadataClassification?: AIClassificationMetadata;
  displayConfig?: AIClassificationDisplayConfig;
  extractionData?: ExtractionBadgeData;
  /** Opens the document viewer; the badge itself is the entry point. */
  onOpen?: () => void;
}

function ExtractionIndicator({ status }: { status: ExtractionBadgeStatus }) {
  switch (status) {
    case 'PENDING':
      return <Loader2Icon className="w-3 h-3 text-purple-500 animate-spin" />;
    case 'FAILED':
      return <AlertCircleIcon className="w-3 h-3 text-red-500" />;
    default:
      return null;
  }
}

function ExtractionTooltipSection({
  data,
  t,
}: {
  data: ExtractionBadgeData;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const entries = data.fields
    ? Object.entries(data.fields).filter(([, v]) => v != null && v !== '')
    : [];

  return (
    <div className="mt-2 pt-2 border-t border-border">
      {data.status === 'DONE' && entries.length > 0 && (
        <>
          <Text size="xs" weight="medium" className="mb-1">
            {t('documents.extraction.title')}
          </Text>
          <Stack asChild>
            <ul className="text-xs text-muted-foreground gap-0.5">
              {entries.map(([key, value]) => (
                <li key={key}>
                  <Text inline weight="medium">
                    {t(`documents.extraction.fields.${key}`, { defaultValue: key })}
                  </Text>
                  {': '}
                  {String(value)}
                </li>
              ))}
            </ul>
          </Stack>
        </>
      )}
      {data.status === 'DONE' && entries.length === 0 && (
        <Text size="xs" tone="muted">
          {t('documents.extraction.noFields')}
        </Text>
      )}
      {data.status === 'PENDING' && (
        <Text size="xs" tone="muted">
          {t('documents.extraction.status.pending')}
        </Text>
      )}
      {data.status === 'FAILED' && (
        <Text size="xs" tone="muted">
          {data.errorMessage
            ? formatExtractionError(data.errorMessage, t)
            : t('documents.extraction.status.failed')}
        </Text>
      )}
      {data.status === 'SKIPPED' && (
        <Text size="xs" tone="muted">
          {t('documents.extraction.status.skipped')}
        </Text>
      )}
      {data.status === 'FAILED' && data.onRetry && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-2 h-7 text-xs"
          onClick={e => {
            e.stopPropagation();
            data.onRetry?.();
          }}
          disabled={data.isRetrying}
        >
          <RotateCwIcon className={cn('w-3 h-3 mr-1', data.isRetrying && 'animate-spin')} />
          {t('documents.extraction.retry')}
        </Button>
      )}
    </div>
  );
}

export function AIClassificationBadge({
  classificationState,
  metadataClassification,
  displayConfig,
  extractionData,
  onOpen,
}: AIClassificationBadgeProps) {
  const { t } = useTranslation();

  if (isDocumentClassificationProcessing(classificationState)) {
    return <DocumentProcessingStatus onOpen={onOpen} />;
  }

  if (classificationState?.status === 'scan_infected') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Group
            gap={0}
            className="gap-1.5 px-2 py-1 rounded-md bg-red-50 border border-red-200 cursor-help"
          >
            <ShieldAlertIcon className="w-3 h-3 text-red-600" />
            <Text inline size="xs" weight="medium" className="text-red-700">
              {t('aiClassification.scanInfected')}
            </Text>
          </Group>
        </TooltipTrigger>
        <TooltipContent>
          <p>{t('aiClassification.scanInfectedTooltip')}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (classificationState?.status === 'scan_timeout') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Group
            gap={0}
            className="gap-1.5 px-2 py-1 rounded-md bg-amber-50 border border-amber-200 cursor-help"
          >
            <AlertCircleIcon className="w-3 h-3 text-amber-600" />
            <Text inline size="xs" weight="medium" className="text-amber-700">
              {t('aiClassification.scanTimeout')}
            </Text>
          </Group>
        </TooltipTrigger>
        <TooltipContent>
          <p>{t('aiClassification.scanTimeoutTooltip')}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (classificationState?.status === 'error') {
    const errorCode = classificationState.error?.code;
    const isTimeout = errorCode === 'REQUEST_TIMEOUT';
    const isServiceUnavailable =
      errorCode === 'SERVICE_UNAVAILABLE' || errorCode === 'AI_PROVIDER_UNAVAILABLE';
    const isDocumentTooLarge = errorCode === 'AI_DOCUMENT_TOO_LARGE';
    const errorLabel =
      isTimeout || isServiceUnavailable
        ? t('aiClassification.classificationUnavailable')
        : t('aiClassification.classificationFailed');
    const fileSizeMb = isDocumentTooLarge
      ? (() => {
          const match = classificationState.error?.message?.match(/(\d+) bytes exceeds/);
          return match ? (Number(match[1]) / (1024 * 1024)).toFixed(1).replace('.', ',') : null;
        })()
      : null;
    const errorMessage =
      isTimeout || isServiceUnavailable
        ? t('aiClassification.classificationUnavailableMessage')
        : isDocumentTooLarge
          ? t('aiClassification.fileTooLarge', {
              fileSize: fileSizeMb,
              maxSize: '3,75',
              maxSizePdf: '32',
            })
          : classificationState.error?.message || t('aiClassification.genericError');

    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-50 border border-amber-200 cursor-pointer"
          >
            <AlertCircleIcon className="w-3 h-3 text-amber-600" />
            <Text inline size="xs" weight="medium" className="text-amber-700">
              {errorLabel}
            </Text>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto max-w-xs p-3 text-sm">
          <p>{errorMessage}</p>
          {extractionData && <ExtractionTooltipSection data={extractionData} t={t} />}
        </PopoverContent>
      </Popover>
    );
  }

  // Show completed transient state or persisted metadata
  const result = classificationState?.status === 'completed' ? classificationState.result : null;
  const isManuallyOverridden = metadataClassification?.status === 'manually_corrected';
  const category = result?.category ?? metadataClassification?.suggestedCategory;
  const qualityFlags = result?.qualityFlags ?? metadataClassification?.qualityFlags ?? [];

  // Filter quality flags based on config
  const filteredQualityFlags =
    displayConfig?.showQualityFlags && displayConfig.qualityFlagsToShow.length > 0
      ? qualityFlags.filter(flag => (displayConfig.qualityFlagsToShow as string[]).includes(flag))
      : displayConfig?.showQualityFlags
        ? qualityFlags
        : [];

  if (isManuallyOverridden && category) {
    return (
      <AiBadgeButton
        icon={<PenIcon className="w-3.5 h-3.5 text-amber-700" />}
        toneClassName="border-amber-200 bg-amber-50 text-amber-800"
        badgeCount={filteredQualityFlags.length}
        extractionData={extractionData}
        onOpen={onOpen}
        t={t}
      />
    );
  }

  if (category) {
    return (
      <AiBadgeButton
        icon={<SparklesIcon className="w-3.5 h-3.5 text-amber-700" />}
        toneClassName="border-amber-200 bg-amber-50 text-amber-800"
        badgeCount={filteredQualityFlags.length}
        extractionData={extractionData}
        onOpen={onOpen}
        t={t}
      />
    );
  }

  // Extraction-only badge (no classification data)
  if (extractionData) {
    const extractionToneConfig: Record<ExtractionBadgeStatus, string> = {
      PENDING: 'border-amber-200 bg-amber-50 text-amber-800',
      DONE: 'border-amber-200 bg-amber-50 text-amber-800',
      FAILED: 'border-red-300 bg-red-100 text-red-800',
      SKIPPED: 'border-amber-200 bg-amber-50 text-amber-800',
    };

    return (
      <AiBadgeButton
        icon={<SparklesIcon className="w-3.5 h-3.5" />}
        toneClassName={extractionToneConfig[extractionData.status]}
        extractionData={extractionData}
        onOpen={onOpen}
        t={t}
      />
    );
  }

  return null;
}

function AiBadgeButton({
  icon,
  toneClassName,
  badgeCount,
  extractionData,
  onOpen,
  t,
}: {
  icon: ReactNode;
  toneClassName: string;
  badgeCount?: number;
  extractionData?: ExtractionBadgeData;
  onOpen?: () => void;
  t: (key: string) => string;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'flex h-[30px] items-center gap-1.5 rounded-md border px-2.5 text-xs font-semibold transition-colors',
        toneClassName,
        onOpen ? 'cursor-pointer hover:brightness-95' : 'cursor-default',
      )}
    >
      {icon}
      <span className="whitespace-nowrap">{t('aiClassification.viewAnalysis')}</span>
      {!!badgeCount && (
        <Text
          inline
          size="xs"
          weight="bold"
          className="flex items-center justify-center w-4 h-4 rounded-full bg-amber-200 text-amber-800"
        >
          {badgeCount}
        </Text>
      )}
      {extractionData && <ExtractionIndicator status={extractionData.status} />}
    </button>
  );
}
